"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import {
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react"
import { Z } from "@/lib/z-index"
import "./glass-side-panel.less"

/**
 * GlassSidePanel — A reusable glass-morphism side panel with optional morphing capsule toggle.
 *
 * Features:
 *  - Supports both left and right placement
 *  - Optional Apple Maps-style capsule: circular icon button when open → floating pill when collapsed
 *  - Optional overlay backdrop (for mobile)
 *  - GPU-accelerated morph animation (transform-based)
 *  - Concentric radius system (22px panel, 14px inner items)
 *  - Glass-on-glass elevation for the capsule button
 *
 * @example
 *  <GlassSidePanel side="left" open={isOpen} onOpenChange={setOpen} capsuleLabel="使用案例">
 *    {content}
 *  </GlassSidePanel>
 */
export interface GlassSidePanelProps {
  /** Which side of the screen */
  side: "left" | "right"
  /** Controlled open state */
  open: boolean
  /** Toggle callback */
  onOpenChange: (open: boolean) => void
  /** Label shown in the collapsed floating pill */
  capsuleLabel?: string
  /** Whether to show the morphing capsule button (default true) */
  showCapsule?: boolean
  /** Whether to show a backdrop overlay when open (default false) */
  showOverlay?: boolean
  /** Extra className for the overlay */
  overlayClassName?: string
  /** Panel width — rem number (default 16) or CSS class string like 'max-w-md' */
  width?: number
  /** Additional max-width CSS class (e.g. 'max-w-md') */
  maxWidth?: string
  /** Margin from screen edge in rem (default 0.75 = 12px) */
  margin?: number
  /** 'center' = vertically centered, max-h 50dvh; 'full' = fills height with margin */
  layout?: "center" | "full"
  /** Additional className on the panel container */
  className?: string
  /** Panel-specific CSS class for glass styling (e.g. 'demo-scenarios-panel') */
  panelStyleClass?: string
  /** z-index for the panel (capsule is zIndex + 1) */
  zIndex?: number
  /** Top offset for full layout (CSS value, default '0.75rem'). Use to clear nav-pill. */
  topOffset?: string
  /** Use flat styling instead of glass-morphism */
  flat?: boolean
  /** Additional className on the capsule button */
  capsuleClassName?: string
  children: React.ReactNode
}

// ── Sizing constants ──
const CAPSULE_SZ = 34       // px — always circular
const CAPSULE_INSET = 8     // px — concentric with panel: 22px radius - 8px gap

const EASE_OUT = "cubic-bezier(0.22, 1, 0.36, 1)"

export function GlassSidePanel({
  side,
  open,
  onOpenChange,
  capsuleLabel = "Panel",
  showCapsule = true,
  showOverlay = false,
  overlayClassName = "",
  width = 16,
  maxWidth,
  margin = 0.75,
  layout = "center",
  className = "",
  panelStyleClass = "",
  zIndex = Z.NAV_PILL - 2,
  topOffset = "0.75rem",
  flat = false,
  capsuleClassName = "",
  children,
}: GlassSidePanelProps) {
  const isLeft = side === "left"

  // ── Panel position & animation ──
  const panelStyle: React.CSSProperties = {
    zIndex,
    contain: "layout style",
    ...(layout === "center"
      ? {
          transform: open
            ? "translateX(0) translateY(-50%)"
            : `translateX(${isLeft ? "calc(-100% - 12px)" : "calc(100% + 12px)"}) translateY(-50%)`,
        }
      : {
          transform: open
            ? "translateX(0)"
            : `translateX(${isLeft ? "calc(-100% - 12px)" : "calc(100% + 12px)"})`,
        }),
    opacity: open ? 1 : 0,
    transition: open
      ? `transform 400ms ${EASE_OUT}, opacity 250ms ease-out`
      : `transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-in`,
  }

  const sideKey = isLeft ? "left" : "right"
  const marginPx = `${margin}rem`

  const panelPositionClass = layout === "center"
    ? "!fixed top-1/2 max-h-[50dvh]"
    : "!fixed"

  const panelPositionStyle: React.CSSProperties = {
    [sideKey]: marginPx,
    ...(layout === "center"
      ? {}
      : { top: topOffset, bottom: marginPx }),
  }

  // ── Capsule button position ──
  const capsuleTop = layout === "center"
    ? `calc(50dvh - 25dvh + ${CAPSULE_INSET}px)`
    : `calc(${topOffset} + ${CAPSULE_INSET}px)`

  const capsuleTransform = (() => {
    if (open) {
      const offset = `calc(${width}rem - ${CAPSULE_SZ + CAPSULE_INSET}px)`
      return isLeft ? `translateX(${offset})` : `translateX(calc(-${offset}))`
    }
    return "translateX(0)"
  })()

  const CloseIcon = isLeft ? PanelLeftClose : PanelRightClose
  const OpenIcon = isLeft ? PanelLeftOpen : PanelRightOpen

  const capsuleGlassClass = flat
    ? (open ? "glass-panel-capsule--flat-open" : "glass-panel-capsule--flat-closed")
    : (open ? "glass-panel-capsule--glass-open" : "glass-panel-capsule--glass-closed")

  // Padding centres the icon when open (circle) and adds pill spacing when closed.
  // (34 - 16) / 2 = 9px centres the 16px icon in the 34px circle.
  const capsulePadL = open ? 9 : 10
  const capsulePadR = open ? 9 : 12

  const capsuleStyle: React.CSSProperties = {
    zIndex: zIndex + 1,
    top: capsuleTop,
    [isLeft ? "left" : "right"]: `${margin}rem`,
    transform: capsuleTransform,
    minWidth: CAPSULE_SZ,
    height: CAPSULE_SZ,
    borderRadius: 9999,
    paddingLeft: capsulePadL,
    paddingRight: capsulePadR,
    willChange: "transform",
    contain: "layout style",
    transition: flat
      ? (open
          ? `transform 400ms ${EASE_OUT}, background 300ms, border 200ms, padding 350ms ${EASE_OUT}`
          : `transform 500ms ${EASE_OUT}, background 300ms, border 200ms, padding 400ms ${EASE_OUT}`)
      : (open
          ? `transform 400ms ${EASE_OUT}, box-shadow 300ms, color 150ms, background 300ms, border 200ms, padding 350ms ${EASE_OUT}`
          : `transform 500ms ${EASE_OUT}, box-shadow 300ms, color 150ms, background 300ms, border 200ms, padding 400ms ${EASE_OUT}`),
  }

  const widthStyle = maxWidth ? { width: "100%" } : { width: `${width}rem` }

  const [portalReady, setPortalReady] = useState(false)
  useEffect(() => { setPortalReady(true) }, [])
  if (!portalReady) return null

  return createPortal(
    <>
      {/* ── Optional overlay backdrop ── */}
      {showOverlay && (
        <div
          className={`liquid-glass-overlay !fixed inset-0 ${overlayClassName}`}
          style={{
            zIndex: zIndex - 1,
            opacity: open ? 1 : 0,
            pointerEvents: open ? "auto" : "none",
            transition: "opacity 300ms ease-out",
          }}
          onClick={() => onOpenChange(false)}
        />
      )}

      {/* ── Panel shell ── */}
      <div
        className={`${flat ? "bg-card border border-border/40 rounded-xl" : "liquid-glass-panel rounded-[22px]"} ${panelPositionClass} ${maxWidth ?? ""} w-full overflow-hidden flex flex-col ${panelStyleClass} ${className}`}
        style={{
          ...widthStyle,
          ...panelPositionStyle,
          ...panelStyle,
        }}
      >
        {children}
      </div>

      {/* ── Optional morphing capsule ── */}
      {showCapsule && (
        <button
          onClick={() => onOpenChange(!open)}
          className={`!fixed flex items-center overflow-hidden text-foreground/60 hover:text-foreground cursor-pointer ${capsuleGlassClass} ${capsuleClassName}`}
          style={capsuleStyle}
          aria-label={open ? `收起${capsuleLabel}` : `展开${capsuleLabel}`}
        >
          {/* Label — collapses to zero width when panel is open */}
          <span
            style={{
              display: "inline-flex",
              maxWidth: open ? 0 : "10rem",
              opacity: open ? 0 : 1,
              overflow: "hidden",
              whiteSpace: "nowrap",
              marginRight: open ? 0 : 6,
              transition: open
                ? `max-width 250ms ${EASE_OUT}, opacity 150ms ease-out, margin 250ms ${EASE_OUT}`
                : `max-width 400ms ${EASE_OUT} 80ms, opacity 300ms ease-out 120ms, margin 400ms ${EASE_OUT} 80ms`,
            }}
          >
            <span className="text-[13px] font-medium">{capsuleLabel}</span>
          </span>

          {/* Icon — crossfade between close (open) and open (closed) */}
          <span className="relative h-4 w-4 shrink-0">
            <CloseIcon
              className="absolute inset-0 h-4 w-4 transition-opacity duration-200"
              style={{ opacity: open ? 0.7 : 0 }}
            />
            <OpenIcon
              className="absolute inset-0 h-4 w-4 transition-opacity duration-200"
              style={{ opacity: open ? 0 : 1 }}
            />
          </span>
        </button>
      )}
    </>,
    document.body
  )
}
